import React, { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../../services/auth';
import { showError, showSuccess } from '../Alert';
import '../../styles/Login.css';
import '../../styles/LoginForm.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token')?.trim() || '', [
    searchParams,
  ]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!token) {
      showError('Reset token is missing or invalid');
      return;
    }

    if (!newPassword.trim() || !confirmPassword.trim()) {
      showError('Please complete both password fields');
      return;
    }

    if (newPassword.trim().length < 8) {
      showError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      await resetPassword({ token, newPassword: newPassword.trim() });
      setIsComplete(true);
      showSuccess('Password has been reset successfully');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to reset password';
      showError(typeof message === 'string' ? message : 'Request failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box text-bg-light rounded-5">
        <h3 className="text-center pt-2 mt-2 fw-semibold">Reset Password</h3>
        <hr />
        <div className="login-form-wrapper">
          {!token ? (
            <div className="auth-message-block">
              <p className="auth-helper-text">
                This password reset link is invalid or missing a token.
              </p>
              <div className="signup-text">
                <Link to="/forgot-password" className="link">
                  Request a new reset link
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <p className="auth-helper-text">
                Enter your new password below.
              </p>

              <div className="input-group mt-3">
                <label htmlFor="newPassword" className="input-group-text">
                  <i className="bi bi-key-fill"></i>
                </label>
                <input
                  id="newPassword"
                  type="password"
                  className="form-control input-box"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewPassword(e.target.value)
                  }
                  required
                  disabled={isLoading || isComplete}
                />
              </div>

              <div className="input-group mt-3">
                <label htmlFor="confirmPassword" className="input-group-text">
                  <i className="bi bi-shield-lock-fill"></i>
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-control input-box"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  required
                  disabled={isLoading || isComplete}
                />
              </div>

              <div className="my-4 text-center">
                <button
                  type="submit"
                  className="btn btn-lg btn-outline-dark"
                  disabled={isLoading || isComplete}
                >
                  {isLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : isComplete ? (
                    'Password Reset'
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>

              <div className="signup-text">
                <Link to="/login" className="link">
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
